import { isEmpty, isEqual } from "lodash-es";
import * as Y from "yjs";

import type { Condition, Segment, SegmentJson } from "../../../types/segment-schema";
import type { SegmentManagerAction } from "./actions";

// The segment as a Yjs document, laid out as segment-engine's src/segment-doc.ts lays it out:
// TODO: Could we share the types so we don't need to re-declare?
//
//   segment: Y.Map                       id, name, description, is_default, is_recipient_type,
//                                        operation, segment_json
//     segment_json: Y.Map                type, operation, segments (or view, for magic)
//       segments: Y.Array<Y.Map>         type, operation, recipient_type_key, conditions
//         conditions: Y.Array<Y.Map>     field, operation, invert, values
//           values: Y.Array<string>
//   ui: Y.Map                            save_for_reuse

type SegmentGroup = Segment["segment_json"]["segments"][number];

/** A segment as the document holds it, which may be a magic one. */
export type DocSegment = Omit<Segment, "segment_json"> & { segment_json: SegmentJson };

const DEFAULT_CONDITION: Condition = { field: "", operation: "IS", values: [], invert: false };

const DEFAULT_GROUP: SegmentGroup = {
  type: "segment_group",
  operation: "AND",
  recipient_type_key: null,
  conditions: [DEFAULT_CONDITION],
};

function conditionToMap(condition: Condition): Y.Map<unknown> {
  const map = new Y.Map<unknown>();
  map.set("field", condition.field);
  map.set("operation", condition.operation);
  map.set("invert", condition.invert);
  map.set("values", Y.Array.from(condition.values));
  return map;
}

function groupToMap(group: SegmentGroup): Y.Map<unknown> {
  const map = new Y.Map<unknown>();
  map.set("type", "segment_group");
  map.set("operation", group.operation);
  map.set("recipient_type_key", group.recipient_type_key ?? null);
  map.set("conditions", Y.Array.from(group.conditions.map(conditionToMap)));
  return map;
}

export const isSeeded = (doc: Y.Doc): boolean => doc.getMap("segment").has("id");

/** The segment in the document, or null before it is seeded. Magic segments come back as is. */
export function readSegment(doc: Y.Doc): DocSegment | null {
  if (!isSeeded(doc)) return null;
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the layout above, seeded by segment-engine
  return doc.getMap("segment").toJSON() as DocSegment;
}

export const readSaveForReuse = (doc: Y.Doc): boolean =>
  doc.getMap("ui").get("save_for_reuse") === true;

export function setSaveForReuse(doc: Y.Doc, value: boolean): void {
  doc.getMap("ui").set("save_for_reuse", value);
}

// oxlint-disable typescript/no-unsafe-type-assertion -- the document only holds the layout above
function groups(doc: Y.Doc): Y.Array<Y.Map<unknown>> | null {
  const json = doc.getMap("segment").get("segment_json") as Y.Map<unknown> | undefined;
  if (json?.get("type") !== "segment") return null;
  return json.get("segments") as Y.Array<Y.Map<unknown>>;
}

function conditions(doc: Y.Doc, groupIndex: number): Y.Array<Y.Map<unknown>> | null {
  return (groups(doc)?.get(groupIndex)?.get("conditions") as Y.Array<Y.Map<unknown>>) ?? null;
}
// oxlint-enable typescript/no-unsafe-type-assertion

// The dialog's own state (showing errors, saving), which stays in its reducer.
const DIALOG_ACTIONS = [
  "SET_ERRORS_DISPLAY",
  "SET_SAVING",
  "FINISHED_SAVING",
  "CLEAR_SAVE_ERROR",
  "SET_SCHEMA",
  "RESET",
] as const;

type DialogAction = Extract<
  SegmentManagerAction<Segment>,
  { type: (typeof DIALOG_ACTIONS)[number] }
>;

const isDialogAction = (action: SegmentManagerAction<Segment>): action is DialogAction =>
  DIALOG_ACTIONS.some((type) => type === action.type);

/**
 * Applies one of the dialog's reducer actions to the document, in one transaction, so it reaches
 * every other editor as a single update. Returns false for actions about the dialog rather than
 * the segment, which the dialog handles itself.
 */
export function applyAction(doc: Y.Doc, action: SegmentManagerAction<Segment>): boolean {
  if (isDialogAction(action)) return false;
  doc.transact(() => applySegmentAction(doc, action));
  return true;
}

// Fields are set in place rather than replacing their map, so two people changing different parts
// of a condition don't overwrite each other.
function applySegmentAction(
  doc: Y.Doc,
  action: Exclude<SegmentManagerAction<Segment>, DialogAction>,
): void {
  const segment = doc.getMap("segment");

  switch (action.type) {
    case "SET_NAME":
      segment.set("name", action.payload);
      break;
    case "SET_DESCRIPTION":
      segment.set("description", action.payload);
      break;
    case "SET_OPERATION":
      segment.set("operation", action.payload);
      break;
    case "ADD_GROUP":
      groups(doc)?.push((action.payload ?? [DEFAULT_GROUP]).map(groupToMap));
      break;
    case "REMOVE_GROUP": {
      const list = groups(doc);
      if (list && action.payload < list.length) list.delete(action.payload, 1);
      break;
    }
    case "UPDATE_GROUP_OPERATION":
      groups(doc)?.get(action.payload.index)?.set("operation", action.payload.operation);
      break;
    case "UPDATE_GROUP_AUDIENCE":
      groups(doc)
        ?.get(action.payload.index)
        ?.set(
          "recipient_type_key",
          isEmpty(action.payload.recipient_type_key) ? null : action.payload.recipient_type_key,
        );
      break;
    case "ADD_CONDITION":
      conditions(doc, action.payload.groupIndex)?.push([conditionToMap(DEFAULT_CONDITION)]);
      break;
    case "REMOVE_CONDITION": {
      const list = conditions(doc, action.payload.groupIndex);
      if (list && action.payload.conditionIndex < list.length) {
        list.delete(action.payload.conditionIndex, 1);
      }
      break;
    }
    case "UPDATE_CONDITION": {
      const map = conditions(doc, action.payload.groupIndex)?.get(action.payload.conditionIndex);
      if (!map) break;
      const { field, operation, invert, values } = action.payload.condition;
      if (map.get("field") !== field) map.set("field", field);
      if (map.get("operation") !== operation) map.set("operation", operation);
      if (map.get("invert") !== invert) map.set("invert", invert);
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see the layout above
      const current = map.get("values") as Y.Array<string>;
      if (!isEqual(current.toJSON(), values)) {
        current.delete(0, current.length);
        current.push(values);
      }
      break;
    }
  }
}
